import React, { useCallback, useEffect, useRef, useState } from "react";
import { formatReply, tidyPartial } from "./formatReply";
import { Link } from "react-router-dom";
import { isRegisteredVisitor, onOpenAivioChat, resolveHref, streamChat } from "./chatApi";
import { followUps } from "./followUps";
import "./AivioChat.css";

const UNAVAILABLE = "I couldn’t reach Aivio right now. Please try again in a moment.";
const SOURCES_VISIBLE = 3;
const FILE_LABEL = { pdf: "PDF", xls: "XLS", doc: "DOC" };

const tiles = [
  { title: "2021 budget", detail: "Key highlights of the Union Budget", prompt: "Tell me about the 2021 Budget.", icon: "chart" },
  { title: "Policy changes", detail: "What changed in 2022", prompt: "What were the major policy changes in 2022?", icon: "shield" },
  { title: "Compare years", detail: "Defence budget, 2021 to 2023", prompt: "Compare the defence budget from 2021 to 2023.", icon: "doc" },
  { title: "Latest budget", detail: "2026–⁠27 highlights · log in", prompt: "What are the key highlights of the Union Budget 2026-27?", icon: "drop" },
];

const icons = {
  spark: <svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l1.9 5.6L19.5 9.5 13.9 11.4 12 17l-1.9-5.6L4.5 9.5l5.6-1.9L12 2zm7 11 1 2.8 2.8 1-2.8 1L19 20.6l-1-2.8-2.8-1 2.8-1L19 13z" /></svg>,
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  newChat: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>,
  expand: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" /></svg>,
  collapse: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7" /></svg>,
  minimize: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6" /></svg>,
  send: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19V5M5 12l7-7 7 7" /></svg>,
  stop: <svg viewBox="0 0 24 24" fill="currentColor"><rect x="7" y="7" width="10" height="10" rx="2" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>,
  copy: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>,
  open: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" /></svg>,
  prev: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 6-6 6 6 6" /></svg>,
  next: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 6 6 6-6 6" /></svg>,
  lock: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg>,
  chart: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></svg>,
  shield: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3 4 6v6c0 4.5 3.4 8.2 8 9 4.6-.8 8-4.5 8-9V6l-8-3Z" /><path d="m9 12 2 2 4-4" /></svg>,
  doc: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z" /><path d="M14 3v5h5M9 13h6M9 17h4" /></svg>,
  drop: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><path d="m7 15 4-4 3 3 6-6" /></svg>,
};

const Mark = ({ className = "" }) => <span className={`ac-mark ${className}`} aria-hidden="true">{icons.spark}</span>;

const greetingFor = () => {
  const hour = new Date().getHours();
  return hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
};
const timeNow = () => new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
const docTitle = (ref) => {
  const title = String(ref.title || "").trim();
  return !title ? "Document" : /^\d+$/.test(title) ? `Document ${title}` : title;
};
const readSession = (key) => { try { return sessionStorage.getItem(key); } catch { return null; } };
const writeSession = (key, value) => { try { sessionStorage.setItem(key, value); } catch { /* storage unavailable */ } };

let nextId = 0;
const newId = () => { nextId += 1; return nextId; };

function Sources({ refs, activeIndex, onOpen }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? refs : refs.slice(0, SOURCES_VISIBLE);
  return (
    <div className="ac-sources">
      <div className="ac-sources-label">{refs.length === 1 ? "Source" : `${refs.length} sources`}</div>
      <div className="ac-source-row">
        {visible.map((ref, index) => (
          <button type="button" className={`ac-source${activeIndex === index ? " is-active" : ""}`} key={`${ref.title}-${ref.href}`} onClick={() => onOpen(index)} title={docTitle(ref)}>
            <span className="ac-source-n">{index + 1}</span>
            <span className={`ac-ft ${ref.kind || "doc"}`}>{FILE_LABEL[ref.kind] || "DOC"}</span>
            <span className="ac-source-name">{docTitle(ref)}</span>
            {ref.page && <span className="ac-source-meta">p. {ref.page}</span>}
          </button>
        ))}
        {!expanded && refs.length > SOURCES_VISIBLE && <button type="button" className="ac-source-more" onClick={() => setExpanded(true)}>+{refs.length - SOURCES_VISIBLE} more</button>}
      </div>
    </div>
  );
}

function BotMessage({ message, viewer, onOpenSource, onCopy, copied }) {
  const showThinking = !message.text && message.streaming;
  const html = message.text ? formatReply(message.streaming ? tidyPartial(message.text) : message.text) : "";
  const handleClick = (event) => {
    const cite = event.target.closest("[data-cite]");
    if (cite) onOpenSource(message.id, Number(cite.dataset.cite) - 1);
  };
  return (
    <div className="ac-m ac-b">
      <div className="ac-who"><Mark /><b>Aivio</b><time>{message.time}</time></div>
      {showThinking ? (
        <div className="ac-thinking" role="status"><span className="ac-dots"><i /><i /><i /></span><span>{message.status || "Understanding your question"}…</span></div>
      ) : (
        <>
          {message.usedSearch && <div className="ac-read">{icons.check}<span>Searched the knowledge base</span></div>}
          <div className={`ac-bub${message.streaming ? " is-streaming" : ""}`} onClick={handleClick} dangerouslySetInnerHTML={{ __html: html }} />
        </>
      )}
      {message.refs.length > 0 && !message.streaming && (
        <Sources refs={message.refs} activeIndex={viewer?.messageId === message.id ? viewer.index : -1} onOpen={(index) => onOpenSource(message.id, index)} />
      )}
      {!message.streaming && message.text && (
        <div className="ac-acts">
          <button type="button" className="ac-ab" onClick={() => onCopy(message)} aria-label="Copy answer" data-tip={copied ? "Copied" : "Copy"}>{copied ? icons.check : icons.copy}</button>
        </div>
      )}
    </div>
  );
}

function SoftKeys({ message, onAsk, onLogin }) {
  const { keys, locked } = followUps(message.question, message.text, isRegisteredVisitor());
  if (!keys.length && !locked) return null;
  return (
    <div className="ac-soft" aria-label="Suggested follow-up questions">
      {locked && <Link to="/login" className="ac-soft-key is-login" onClick={onLogin}>{icons.lock}Log in for 2023–⁠24 to 2026–⁠27</Link>}
      {keys.map((key) => <button type="button" className="ac-soft-key" key={key.prompt} onClick={() => onAsk(key.prompt)}>{key.label}</button>)}
    </div>
  );
}

function SourceViewer({ refs, index, onMove, onClose }) {
  const ref = refs[index];
  if (!ref) return null;
  const href = resolveHref(ref.href);
  const pdfSrc = href && ref.kind === "pdf" ? `${href}#${ref.page ? `page=${encodeURIComponent(ref.page)}&` : ""}view=FitH` : "";
  return (
    <aside className="ac-viewer" aria-label="Source document preview">
      <div className="ac-vh">
        <span className="ac-vh-t">Source {index + 1} of {refs.length}</span>
        <button type="button" className="ac-ib" onClick={() => onMove(-1)} disabled={index === 0} aria-label="Previous source" data-tip="Previous">{icons.prev}</button>
        <button type="button" className="ac-ib" onClick={() => onMove(1)} disabled={index === refs.length - 1} aria-label="Next source" data-tip="Next">{icons.next}</button>
        <span className="ac-vsep" />
        {href && <a className="ac-ib" href={href} target="_blank" rel="noopener noreferrer" aria-label="Open in new tab" data-tip="Open in new tab">{icons.open}</a>}
        <button type="button" className="ac-ib" onClick={onClose} aria-label="Close preview" data-tip="Close">{icons.close}</button>
      </div>
      <div className="ac-vtop">
        <div className="ac-vtitle"><span className={`ac-ft ${ref.kind || "doc"}`}>{FILE_LABEL[ref.kind] || "DOC"}</span>{docTitle(ref)}</div>
        {ref.page && <div className="ac-vmeta">Page {ref.page}</div>}
        {ref.snippet && <div className="ac-quote"><small>Relevant passage</small>{ref.snippet}</div>}
      </div>
      <div className="ac-vstage">
        {pdfSrc ? <iframe title={docTitle(ref)} src={pdfSrc} /> : (
          <div className="ac-nopreview"><p>A preview isn’t available for this document.</p>{href && <a href={href} target="_blank" rel="noopener noreferrer">Open the document {icons.open}</a>}</div>
        )}
      </div>
    </aside>
  );
}

function AivioChat() {
  const [open, setOpen] = useState(false);
  const [wide, setWide] = useState(false);
  const [greeting, setGreeting] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [viewer, setViewer] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [stick, setStick] = useState(true);
  const threadRef = useRef(null);
  const accessRef = useRef(null);
  const abortRef = useRef(null);
  const bodyRef = useRef(null);
  const inputRef = useRef(null);
  const fabRef = useRef(null);
  const greetDismissed = useRef(false);

  const updateMessage = useCallback((id, change) => {
    setMessages((list) => list.map((m) => (m.id === id ? { ...m, ...(typeof change === "function" ? change(m) : change) } : m)));
  }, []);

  const resetChat = useCallback(() => {
    abortRef.current?.abort();
    threadRef.current = null;
    accessRef.current = null;
    setMessages([]);
    setViewer(null);
    setBusy(false);
  }, []);

  const ask = useCallback(async (raw) => {
    const text = raw.trim();
    if (!text || busy) return;
    // A different access level must never continue the previous conversation.
    const registered = isRegisteredVisitor();
    if (accessRef.current !== null && accessRef.current !== registered) {
      threadRef.current = null;
      setMessages([]);
      setViewer(null);
    }
    accessRef.current = registered;

    const botId = newId();
    setMessages((list) => [
      ...list,
      { id: newId(), role: "user", text },
      { id: botId, role: "bot", question: text, text: "", refs: [], status: "", streaming: true, usedSearch: false, time: timeNow() },
    ]);
    setInput("");
    setStick(true);
    setBusy(true);
    const controller = new AbortController();
    abortRef.current = controller;

    try {
      await streamChat({
        message: text,
        threadId: threadRef.current,
        signal: controller.signal,
        onEvent: (event) => {
          if (event.thread_id) threadRef.current = event.thread_id;
          if (event.status) updateMessage(botId, { status: event.status });
          if (event.refs) updateMessage(botId, (m) => ({ refs: [...m.refs, ...event.refs], usedSearch: true }));
          if (event.replace) updateMessage(botId, (m) => ({ text: event.replace, refs: event.clear_refs ? [] : m.refs }));
          if (event.t) updateMessage(botId, (m) => ({ text: m.text + event.t }));
        },
      });
    } catch (error) {
      if (error.name !== "AbortError") updateMessage(botId, (m) => ({ text: m.text || UNAVAILABLE }));
    } finally {
      updateMessage(botId, (m) => ({ streaming: false, text: m.text || (controller.signal.aborted ? "Stopped." : UNAVAILABLE) }));
      if (abortRef.current === controller) abortRef.current = null;
      setBusy(false);
    }
  }, [busy, updateMessage]);

  const openChat = useCallback(() => {
    setOpen(true);
    setGreeting(false);
    writeSession("aivio_chat_seen", "1");
  }, []);

  const closeChat = useCallback(() => {
    setOpen(false);
    setViewer(null);
    fabRef.current?.focus();
  }, []);

  // Other parts of the site open the chat (and optionally ask) through openAivioChat().
  const askRef = useRef(ask);
  askRef.current = ask;
  useEffect(() => onOpenAivioChat((prompt) => {
    openChat();
    if (prompt) askRef.current(prompt);
  }), [openChat]);

  // The greeting returns a few seconds after the chat is closed, until the visitor dismisses it.
  useEffect(() => {
    if (open || greetDismissed.current || !window.matchMedia("(min-width: 768px)").matches) return undefined;
    const timer = window.setTimeout(() => setGreeting(true), 2500);
    return () => window.clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (open) window.setTimeout(() => inputRef.current?.focus(), 250);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key !== "Escape") return;
      if (viewer) setViewer(null);
      else closeChat();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, viewer, closeChat]);

  useEffect(() => {
    if (stick && bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
  }, [messages, stick]);

  useEffect(() => () => abortRef.current?.abort(), []);

  const handleScroll = () => {
    const el = bodyRef.current;
    setStick(el.scrollHeight - el.scrollTop - el.clientHeight < 80);
  };

  const handleInput = (event) => {
    setInput(event.target.value);
    event.target.style.height = "auto";
    event.target.style.height = `${Math.min(event.target.scrollHeight, 120)}px`;
  };

  const submit = (event) => {
    event.preventDefault();
    if (busy) abortRef.current?.abort();
    else {
      ask(input);
      if (inputRef.current) inputRef.current.style.height = "auto";
    }
  };

  const copyAnswer = async (message) => {
    try {
      await navigator.clipboard.writeText(message.text);
      setCopiedId(message.id);
      window.setTimeout(() => setCopiedId(null), 1500);
    } catch { /* clipboard unavailable */ }
  };

  const lastMessage = messages[messages.length - 1];
  const viewerMessage = viewer && messages.find((m) => m.id === viewer.messageId);
  const openSource = (messageId, index) => {
    const message = messages.find((m) => m.id === messageId);
    if (!message?.refs[index]) return;
    setViewer((current) => (current?.messageId === messageId && current.index === index ? null : { messageId, index }));
  };

  const rootClass = ["aivio-chat", open && "is-open", wide && "is-wide", busy && "is-busy", viewerMessage && "is-viewing"].filter(Boolean).join(" ");
  const canSend = input.trim().length > 0;

  return (
    <div className={rootClass}>
      {greeting && !open && (
        <div className="ac-greet" role="status">
          <div className="ac-greet-top"><Mark /><b>Aivio</b><button type="button" className="ac-ib" onClick={() => { greetDismissed.current = true; setGreeting(false); writeSession("aivio_chat_seen", "1"); }} aria-label="Dismiss">{icons.close}</button></div>
          <p>{messages.length ? "Have another question? Pick up where you left off." : "Hi, I’m Aivio. Ask me anything about India’s Union Budget."}</p>
          <div className="ac-greet-q">{tiles.slice(0, 2).map((tile) => <button type="button" key={tile.title} onClick={() => { openChat(); ask(tile.prompt); }}>{tile.title}</button>)}</div>
        </div>
      )}

      <button type="button" ref={fabRef} className="ac-fab" onClick={() => (open ? closeChat() : openChat())} aria-expanded={open} aria-controls="aivio-chat-panel" aria-label={open ? "Close Aivio" : "Open Aivio"}>
        <span className="ac-fi"><span className="ac-i-spark">{icons.spark}</span><span className="ac-i-x">{icons.close}</span>{!readSession("aivio_chat_seen") && <span className="ac-dot" />}</span>
        <span className="ac-lbl">Ask Aivio</span>
      </button>

      <section className="ac-panel" id="aivio-chat-panel" role="dialog" aria-label="Aivio chat" aria-hidden={!open}>
        {viewerMessage && <SourceViewer refs={viewerMessage.refs} index={viewer.index} onMove={(step) => setViewer((v) => ({ ...v, index: v.index + step }))} onClose={() => setViewer(null)} />}

        <div className="ac-chat">
          <header className="ac-ph">
            <Mark />
            <div className="ac-ph-t"><b>Aivio</b><span>Enterprise AI agent</span></div>
            <button type="button" className="ac-ib" onClick={() => { resetChat(); inputRef.current?.focus(); }} aria-label="New chat" data-tip="New chat">{icons.newChat}</button>
            <button type="button" className="ac-ib ac-wide-btn" onClick={() => setWide((w) => !w)} aria-label={wide ? "Collapse" : "Expand"} data-tip={wide ? "Collapse" : "Expand"}>{wide ? icons.collapse : icons.expand}</button>
            <button type="button" className="ac-ib" onClick={closeChat} aria-label="Minimize" data-tip="Minimize">{icons.minimize}</button>
          </header>

          <div className="ac-pb" ref={bodyRef} onScroll={handleScroll} aria-live="polite">
            {messages.length === 0 ? (
              <div className="ac-welcome">
                <div className="ac-welcome-brand"><Mark /><span>Aivio · India Budget demo</span></div>
                <h2 className="ac-hi">{greetingFor()}.<span>What would you like to know?</span></h2>
                <p>Ask about the Union Budget in plain language. Every answer comes with its sources.</p>
                <div className="ac-suggest-label">Try asking</div>
                <div className="ac-tiles">
                  {tiles.map((tile) => (
                    <button type="button" className="ac-tile" key={tile.title} onClick={() => ask(tile.prompt)}>
                      <span className="ac-ti">{icons[tile.icon]}</span>
                      <span><b>{tile.title}</b><small>{tile.detail}</small></span>
                    </button>
                  ))}
                </div>
                <div className="ac-trust">{icons.lock}{isRegisteredVisitor() ? "Signed in: budgets up to 2026–⁠27 are included." : "2020–⁠21 to 2022–⁠23 is open to all. Log in to include up to 2026–⁠27."}</div>
              </div>
            ) : (
              <div className="ac-thread">
                {messages.map((message) => (message.role === "user" ? (
                  <div className="ac-m ac-u" key={message.id}><div className="ac-bub">{message.text}</div></div>
                ) : (
                  <BotMessage key={message.id} message={message} viewer={viewer} onOpenSource={openSource} onCopy={copyAnswer} copied={copiedId === message.id} />
                )))}
                {!busy && lastMessage?.role === "bot" && !lastMessage.streaming && lastMessage.text && lastMessage.text !== UNAVAILABLE && lastMessage.text !== "Stopped." && (
                  <SoftKeys message={lastMessage} onAsk={ask} onLogin={closeChat} />
                )}
              </div>
            )}
            {!stick && messages.length > 0 && <button type="button" className="ac-jump" onClick={() => setStick(true)}>Jump to latest ↓</button>}
          </div>

          <footer className="ac-pf">
            <form className="ac-cmp" onSubmit={submit}>
              <textarea ref={inputRef} rows={1} value={input} onChange={handleInput} onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) submit(event); }} placeholder="Ask about the Union Budget…" aria-label="Message Aivio" maxLength={4000} />
              <button type="submit" className={`ac-send${canSend && !busy ? " is-ready" : ""}`} disabled={!canSend && !busy} aria-label={busy ? "Stop" : "Send"}>{busy ? icons.stop : icons.send}</button>
            </form>
            <div className="ac-disc">Aivio can make mistakes. Check sources for important details.</div>
          </footer>
        </div>
      </section>
    </div>
  );
}

export default AivioChat;
